import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environments';

@Injectable({
  providedIn: 'root'
})
export class VersionService {
  private readonly apiUrl = environment.apiUrl + '/v1/gasprecio/versions';
  private readonly localKey = 'appVersionGaspre';
  private readonly reloadGuardKey = 'appVersionGaspreReloadedFor';
  private readonly swReloadGuardKey = 'appSwVersionReloadedFor';
  private readonly staleBundleGuardKey = 'appStaleBundleResetFor';
  // Si la versión nueva queda lista en los primeros segundos, el usuario aún no
  // empezó a trabajar y se recarga de inmediato; después se espera a que cambie
  // de vista para no tirarle una captura a medias.
  private readonly startupWindowMs = 45 * 1000;
  private readonly checkEveryMs = 15 * 60 * 1000;
  private readonly startedAt = Date.now();

  constructor(private http: HttpClient, private swUpdate: SwUpdate, private router: Router) { }

  /**
   * Mantiene la app en la última versión desplegada sin depender del bump manual
   * de `/versions`.
   *
   * El service worker sirve la versión que tiene en caché y descarga la nueva en
   * segundo plano: sin escuchar `VERSION_READY`, el usuario se quedaba con la
   * versión anterior hasta un Ctrl+Shift+R (reporte MOBICO 14-oct). Ahora, en
   * cuanto la versión nueva está descargada se activa y se recarga — una sola vez
   * por versión — y las pestañas abiertas todo el día la buscan cada 15 min.
   */
  watchServiceWorkerUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      // Con el service worker apagado (20-oct) se limpia lo que haya dejado el worker viejo en este
      // navegador, sin recargar: si esta pestaña ya corre este bundle, viene del servidor.
      this.resetServiceWorker();
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe((event) => this.applyReadyVersion(event.latestVersion.hash));

    // El caché del SW quedó inservible (p. ej. borraron bundles del deploy
    // anterior): la única salida es recargar desde la red
    this.swUpdate.unrecoverable.subscribe(() => window.location.reload());

    // El SW puede quedar degradado y no avisar nada (ni VERSION_READY ni unrecoverable): se revisa al arrancar
    this.resetIfStaleBundle(true);

    interval(this.checkEveryMs).subscribe(() => {
      this.swUpdate.checkForUpdate().catch((err) => console.error('Error buscando versión nueva:', err));
      this.resetIfStaleBundle(false);
    });
  }

  /**
   * Sale de un service worker trabado en una versión vieja.
   *
   * Incidente 20-oct (Control de urgencia en blanco al recargar): si un deploy sube el index.html nuevo mientras el SW
   * todavía termina de bajar la versión anterior, el hash no le cuadra y el SW entra en modo degradado
   * (EXISTING_CLIENTS_ONLY): sigue sirviendo la versión vieja de su caché, cuyos archivos ya no existen en el servidor,
   * y nunca emite VERSION_READY. Se compara el main.*.js que corre esta pestaña contra el del index.html del servidor
   * (saltándose el SW con ngsw-bypass); si son distintos se desregistra el SW, se borran sus caches ESPERANDO a que
   * terminen y se recarga desde la red. Una sola vez por versión del servidor en esta pestaña. Al arrancar se recarga
   * de inmediato; en la revisión periódica se espera al siguiente cambio de vista para no tirar una captura a medias.
   */
  private async resetIfStaleBundle(immediate: boolean): Promise<void> {
    const loaded = this.mainBundle(Array.from(document.scripts).map((script) => script.src).join(' '));
    if (!loaded) {
      return; // build de desarrollo (main.js sin hash): no hay con qué comparar
    }
    try {
      const response = await fetch(`/index.html?ngsw-bypass=true&t=${Date.now()}`, { cache: 'no-store' });
      const served = response.ok ? this.mainBundle(await response.text()) : null;
      if (!served || served === loaded || sessionStorage.getItem(this.staleBundleGuardKey) === served) {
        return;
      }
      console.warn(`Versión vieja en caché del service worker: ${loaded}, servidor: ${served}`);
      sessionStorage.setItem(this.staleBundleGuardKey, served);
      if (immediate) {
        await this.resetServiceWorker();
        window.location.reload();
        return;
      }
      this.router.events
        .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart), take(1))
        .subscribe(async (event) => { await this.resetServiceWorker(); window.location.assign(event.url); });
    } catch (err) {
      console.error('Error revisando la versión servida:', err);
    }
  }

  /** Desregistra el SW y borra sus caches (ngsw:*), esperando a que termine antes de seguir. */
  private async resetServiceWorker(): Promise<void> {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.filter((name) => name.startsWith('ngsw:')).map((name) => caches.delete(name)));
      }
    } catch (err) {
      console.error('Error limpiando el service worker:', err);
    }
  }

  /** Nombre del bundle principal con hash (main.1a2b3c.js) dentro de un texto, o null. */
  private mainBundle(text: string): string | null {
    const match = text.match(/main\.[a-f0-9]{8,}\.js/);
    return match ? match[0] : null;
  }

  private applyReadyVersion(hash: string): void {
    // Guard anti-bucle: una sola recarga por versión en esta pestaña
    if (sessionStorage.getItem(this.swReloadGuardKey) === hash) {
      return;
    }

    const reloadNow = Date.now() - this.startedAt < this.startupWindowMs;
    if (reloadNow) {
      this.activateAndGo(hash);
      return;
    }

    // Recarga completa hacia la vista que el usuario acaba de pedir
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart), take(1))
      .subscribe((event) => this.activateAndGo(hash, event.url));
  }

  private async activateAndGo(hash: string, url?: string): Promise<void> {
    sessionStorage.setItem(this.swReloadGuardKey, hash);
    try {
      await this.swUpdate.activateUpdate();
    } catch (err) {
      console.error('Error activando la versión nueva:', err);
    } finally {
      if (url) {
        window.location.assign(url);
      } else {
        window.location.reload();
      }
    }
  }

  checkVersion(): void {
    this.http.get<any>(this.apiUrl).subscribe({
      next: (response: any) => {
        const currentVersion = response.version.toString();
        const localVersion = localStorage.getItem(this.localKey);

        if (localVersion === null) {
          // Primera versión que ve esta sesión: no hay de qué actualizarse. Pasa en
          // cada login, porque `logout()` hace `localStorage.clear()` y se lleva la
          // versión guardada. Recargar aquí era una carga completa del bundle de
          // regalo justo después de entrar —otro tramo en blanco— sin nada que
          // actualizar. Mismo criterio que el guard de assets/js/updateCache.js.
          localStorage.setItem(this.localKey, currentVersion);
          console.info('Primera versión registrada en esta sesión ✅');
        } else if (localVersion !== currentVersion) {
          console.warn(`Versión desactualizada. Local: ${localVersion}, Backend: ${currentVersion}`);
          // Guardar ANTES de recargar: si se guardara después, una recarga que
          // interrumpa el hilo deja el mismatch eterno → bucle de recargas
          localStorage.setItem(this.localKey, currentVersion);
          // Guard anti-bucle: una sola recarga por versión en esta pestaña
          if (sessionStorage.getItem(this.reloadGuardKey) !== currentVersion) {
            sessionStorage.setItem(this.reloadGuardKey, currentVersion);
            this.updateAndReload();
          }
        } else {
          console.info('Versión actualizada. Todo OK ✅');
        }
      },
      error: (err) => {
        console.error('Error verificando versión:', err);
      }
    });
  }

  /**
   * Actualiza a la última versión sin corromper el service worker.
   *
   * La versión anterior borraba TODOS los caches del navegador (incluidos los
   * `ngsw:*` del Angular SW) con el SW aún activo y sin esperar el borrado:
   * el SW quedaba sirviendo desde caches inexistentes → pantalla en blanco
   * para los usuarios tras cada bump de versión (incidente 2026-08-26).
   *
   * Ahora: con SW habilitado se deja que Angular instale y active la versión
   * nueva de forma atómica (checkForUpdate + activateUpdate); sin SW se
   * desregistran los service workers y se borran los caches ESPERANDO a que
   * terminen antes de recargar.
   */
  private async updateAndReload(): Promise<void> {
    try {
      if (this.swUpdate.isEnabled) {
        await this.swUpdate.checkForUpdate();
        await this.swUpdate.activateUpdate();
      } else if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map((name) => caches.delete(name)));
        }
      }
    } catch (err) {
      console.error('Error actualizando la versión:', err);
    } finally {
      window.location.reload();
    }
  }
}
